import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { AuthDialogProvider } from "@/context/AuthDialogContext";

import Landing from "@/pages/Landing";
const Varie = React.lazy(() => import("@/pages/Varie"));
const Generator = React.lazy(() => import("@/pages/Generator"));
const AdminProvenance = React.lazy(() => import("@/pages/AdminProvenance"));
const AssetPassport = React.lazy(() => import("@/pages/AssetPassport"));
const BuildFlow = React.lazy(() => import("@/pages/BuildFlow"));
import IdeaFlow from "@/pages/IdeaFlow";
import Dashboard from "@/pages/Dashboard";
import ProjectDetail from "@/pages/ProjectDetail";
import AuthCallback from "@/pages/AuthCallback";
import { OriginDeclarationHost } from "@/components/OriginDeclaration";
const SharePublic = React.lazy(() => import("@/pages/SharePublic"));
const Checkout = React.lazy(() => import("@/pages/Checkout"));
const Legal = React.lazy(() => import("@/pages/Legal"));
const Admin = React.lazy(() => import("@/pages/Admin"));
const AdminTrafficPage = React.lazy(() => import("@/pages/AdminTrafficPage"));
const GuestPreview = React.lazy(() => import("@/pages/GuestPreview"));
const Newsletter = React.lazy(() => import("@/pages/Newsletter"));
import Home from "@/pages/Home";
const Profile = React.lazy(() => import("@/pages/Profile"));
const Report = React.lazy(() => import("@/pages/Report"));
const IdeaHistoryPage = React.lazy(() => import("@/pages/IdeaHistoryPage"));
const Compare = React.lazy(() => import("@/pages/Compare"));
const Stories = React.lazy(() => import("@/pages/Stories"));
const Methodology = React.lazy(() => import("@/pages/Methodology"));
const Credits = React.lazy(() => import("@/pages/Credits"));
const Roadmap = React.lazy(() => import("@/pages/Roadmap"));
const Community = React.lazy(() => import("@/pages/Community"));
const CommunityIdea = React.lazy(() => import("@/pages/Community").then((m) => ({ default: m.CommunityIdea })));
const Builder = React.lazy(() => import("@/pages/Community").then((m) => ({ default: m.Builder })));
const VerifyRecord = React.lazy(() => import("@/pages/Community").then((m) => ({ default: m.VerifyRecord })));
const Market = React.lazy(() => import("@/pages/Market"));
const Listing = React.lazy(() => import("@/pages/Market").then((m) => ({ default: m.Listing })));
const AssetEditor = React.lazy(() => import("@/pages/AssetEditor"));
const MarketMe = React.lazy(() => import("@/pages/AssetEditor").then((m) => ({ default: m.MarketMe })));
import PageLoader from "@/components/PageLoader";
import { useTheme } from "@/hooks/useTheme";
import { InstallHint } from "@/components/InstallHint";
import { useTracker } from "@/hooks/useTracker";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

import { PortalNav } from "@/components/PortalNav";
import Footer from "@/components/Footer";
import { Navigate } from "react-router-dom";

/** Soft tinted canvas behind every app page (not the landing): white cards and sections stand out from the background. */
function BodyTint() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    const on = pathname !== "/" && pathname !== "/app/build";
    document.body.classList.toggle("app-tint", on);
    return () => document.body.classList.remove("app-tint");
  }, [pathname]);
  return null;
}

function ThemeController() { useTheme(); return null; }

function AppRouter() {
  const location = useLocation();
  useTracker();
  React.useEffect(() => { if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual"; }, []);
  React.useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    const id = requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "instant" }));
    return () => cancelAnimationFrame(id);
  }, [location.pathname, location.search]);
  if (location.hash?.includes("session_id=") || (location.pathname === "/auth/google" && location.search.includes("code="))) {
    return <AuthCallback />;
  }
  return (
    <>
      <BodyTint />
      <ThemeController />
      <PageLoader />
      {location.pathname !== "/" && location.pathname !== "/app/build" && <PortalNav />}
      <InstallHint />
      <React.Suspense fallback={null}>
        <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/varie" element={<Varie />} />
        <Route path="/generatore" element={<Generator />} />
        <Route path="/generator" element={<Generator />} />
        <Route path="/app/build" element={<BuildFlow />} />
        <Route path="/app/home" element={<Home />} />
        <Route path="/app/profile" element={<Profile />} />
        <Route path="/app/report" element={<Report />} />
        <Route path="/idea/:id/history" element={<IdeaHistoryPage />} />
        <Route path="/app/credits" element={<Credits />} />
        <Route path="/app/explore" element={<Navigate to="/community" replace />} />
        <Route path="/auth/google" element={<Navigate to="/" replace />} />
        <Route path="/community" element={<Community />} />
        <Route path="/community/:id" element={<CommunityIdea />} />
        <Route path="/builder/:id" element={<Builder />} />
        <Route path="/verify/:id" element={<VerifyRecord />} />
        <Route path="/market" element={<Market />} />
        <Route path="/market/:assetId" element={<Listing />} />
        <Route path="/app/idea/:id/asset" element={<AssetEditor />} />
        <Route path="/app/market/me" element={<MarketMe />} />
        <Route path="/app/compare/:a/:b" element={<Compare />} />
        <Route path="/preview" element={<GuestPreview />} />
        <Route path="/testimonianze" element={<Stories />} />
        <Route path="/metodologia" element={<Methodology />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/app/new" element={<IdeaFlow />} />
        <Route path="/app/idea/:id" element={<IdeaFlow />} />
        <Route path="/app/dashboard" element={<Dashboard />} />
        <Route path="/app/project/:id" element={<ProjectDetail />} />
        <Route path="/app/checkout/:id" element={<Checkout />} />
        <Route path="/app/checkout" element={<Checkout />} />
        <Route path="/app/admin" element={<Admin />} />
        <Route path="/app/admin/traffic" element={<AdminTrafficPage />} />
        <Route path="/app/admin/provenance" element={<AdminProvenance />} />
        <Route path="/asset/:id" element={<AssetPassport />} />
        <Route path="/newsletter" element={<Newsletter />} />
        <Route path="/newsletter/unsubscribe" element={<Newsletter mode="unsubscribe" />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/legal/:doc" element={<Legal />} />
        <Route path="/share/:id" element={<SharePublic />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
        </React.Suspense>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <AuthDialogProvider>
              <AppRouter />
              <Toaster position="top-center" richColors />
              <OriginDeclarationHost />
            </AuthDialogProvider>
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
